import React, { CSSProperties, useCallback, useEffect, useState } from 'react';

import lang from '../../../lib/language';

import { useIsMobile } from '../../../hooks';
import NoResultsFound from '../../NoResultsFound/NoResultsFound';
import Preloader from '../../Preloader/Preloader';

import styles from './LatestNewsContent.module.scss';

type Props = {
  newsCards: JSX.Element[];
  isLoggedIn: boolean;
  gatewayConnected: boolean;
  gwEnabled: boolean;
  gwNewsForSymbolEnabled: boolean;
  gwLatestNewsEnabled: boolean;
  isMounted: boolean;
  isNewsAvailable: (selectedSymbol?: string, hasMore?: boolean) => boolean;
  getLatestNews: (page?: number, pageSize?: number) => void;
  getNewsForSymbol: (symbol: string, page?: number, pageSize?: number) => void;
  selectedSymbol?: string;
};

const LatestNewsContent = (
  {
    selectedSymbol,
    gwEnabled,
    gwNewsForSymbolEnabled,
    isLoggedIn,
    newsCards,
    gatewayConnected,
    gwLatestNewsEnabled,
    isMounted,
    isNewsAvailable,
    getNewsForSymbol,
    getLatestNews,
  }: Props,
): JSX.Element => {
  let wrapperClass;
  const isMobile = useIsMobile();
  const RSS_FEED_COUNT = 6;

  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [cardsCount, setCardsCount] = useState(RSS_FEED_COUNT);

  const gatewayNewsEnabled = gwEnabled && (selectedSymbol ? gwNewsForSymbolEnabled : gwLatestNewsEnabled);
  const isGatewayDown = gatewayNewsEnabled && !gatewayConnected;

  const preloaderStyles: CSSProperties = { transform: 'scale(0.5)' };

  const loadMore = useCallback(() => {
    const page = !isInitialLoadDone ? 1 : undefined;

    if (selectedSymbol) {
      getNewsForSymbol(selectedSymbol, page);
    } else {
      getLatestNews(page);
    }
  }, [selectedSymbol, isInitialLoadDone, getNewsForSymbol, getLatestNews]);

  useEffect(() => {
    setIsInitialLoadDone(false);
  }, [selectedSymbol]);

  useEffect(() => {
    if (!isInitialLoadDone && isMounted && isLoggedIn) {
      loadMore();
      setIsInitialLoadDone(true);
    }
  }, [isMounted, loadMore, isInitialLoadDone, isLoggedIn]);

  if (!isLoggedIn || !isMounted) {
    return <Preloader style={preloaderStyles} isTextOnly owner="LatestNews-2" />;
  }

  if (selectedSymbol && !isNewsAvailable(selectedSymbol)) {
    return (
      !isGatewayDown ? <NoResultsFound label="News" />
        : <NoResultsFound fullText={lang.newsTechnicalIssueMessage()} />
    );
  }

  const hasMoreNews = selectedSymbol ? isNewsAvailable(selectedSymbol, true) : newsCards.length !== cardsCount;

  if (isMobile) {
    wrapperClass = selectedSymbol
      ? styles.wrapperLatestNewsMobileSymbol
      : styles.wrapperLatestNewsMobile;
  } else {
    wrapperClass = styles.wrapperLatestNews;
  }

  return (
    <div className={wrapperClass}>
      {newsCards.slice(0, selectedSymbol ? newsCards.length : cardsCount)}
      {hasMoreNews && (
        <div className={styles.loadMore}>
          <button
            type="button"
            className={styles.loadMoreBtn}
            onClick={() => {
              if (selectedSymbol) {
                loadMore();
                return;
              }

              setCardsCount((prev) => prev + RSS_FEED_COUNT);
            }}
          >
            {lang.commonLoadMore()}
          </button>
        </div>
      )}
    </div>
  );
};

export default LatestNewsContent;
